import { useState, type ReactNode } from 'react'

/**
 * Ein langer Text, der nicht die ganze Seite belegt.
 *
 * **Der Anlass ist gemessen, nicht vermutet.** Ein Beitrag mit achttausend
 * Zeichen ist auf einem iPhone 6 514 Pixel hoch — 7,7 Bildschirme. Der Text
 * eines Highlights mit sechzehntausend Zeichen sind 11,8 Bildschirme, und was
 * darunter liegt (Fotos, Spots, die Knöpfe des Blattes), fängt erst bei
 * 10 447 Pixeln an. Es scrollt also, es ragt nichts seitlich hinaus — aber
 * niemand kommt mehr an das heran, was unter dem Text steht.
 *
 * **Zusammengelegt, nicht gekürzt.** Der Text bleibt vollständig im DOM; der
 * Deckel ist eine Frage der Darstellung. „Mehr anzeigen" klappt ihn auf,
 * „Weniger" wieder zu. Wer sucht (die Suche des Browsers), findet weiterhin
 * alles — `-webkit-line-clamp` blendet aus, es entfernt nichts.
 *
 * **Auf Papier gibt es keinen Deckel.** Unter `@media print` ist die Klemme
 * aufgehoben und der Knopf weg; ein gedruckter Reisebericht mit einer
 * „Mehr anzeigen"-Zeile wäre albern.
 *
 * Kurze Texte gehen unverändert durch: unter `AB_ZEICHEN` steht gar kein
 * Knopf und keine zusätzliche Hülle mit eigenem Verhalten.
 */
export function Langtext({
  text,
  children,
  zeilen = 12,
  ab = AB_ZEICHEN,
}: {
  /**
   * Der Text — **nur zum Messen.**
   *
   * Gezeichnet wird `children`; dort steckt die Auszeichnung (fett, Links),
   * und die soll der Deckel nicht kennen. Zwei Wege für dieselbe Sache wären
   * zwei Wahrheiten.
   */
  text: string
  children: ReactNode
  /** Wie viele Zeilen im zugeklappten Zustand stehen bleiben. */
  zeilen?: number
  /** Ab wie vielen Zeichen überhaupt geklemmt wird. */
  ab?: number
}) {
  const [offen, setOffen] = useState(false)

  if (text.length < ab) return <>{children}</>

  return (
    <>
      <div
        className={offen ? 'langtext' : 'langtext langtext--zu'}
        style={offen ? undefined : ({ '--langtext-zeilen': zeilen } as React.CSSProperties)}
      >
        {children}
      </div>
      <button
        type="button"
        className="langtext__mehr"
        aria-expanded={offen}
        onClick={(event) => {
          /* **Die Geste endet hier.** Der Deckel sitzt in einer Sprechblase
             und in einem Kartenblatt; beide haben eigene Zuhörer für Tipp und
             Wisch. Wer eine Ebene obendrauf legt, hält ihre Ereignisse dort
             an, statt unten eine Vorsichtsregel einzubauen. */
          event.stopPropagation()
          setOffen((war) => !war)
        }}
      >
        {offen ? 'Weniger' : 'Mehr anzeigen'}
      </button>
    </>
  )
}

/**
 * Ab wann ein Text lang ist.
 *
 * Achthundert Zeichen sind rund eine Bildschirmhöhe auf 390 Punkten — darunter
 * ist der Deckel eine Bedienung ohne Anlass, und der Knopf stünde unter jedem
 * zweiten Beitrag.
 */
export const AB_ZEICHEN = 800
